/** @file Inflates a zlib stream into exactly n bytes, writing straight into them and stopping at n + 1. */
import { inflateRaw, type RawProblem } from './_internal/inflateRaw';
import { inputSegments } from './_internal/inputSegments';

/** Why a stream does not hold exactly the expected bytes. */
export type InflateProblem = 'header' | 'damaged' | 'overflow' | 'short' | 'truncated' | 'trailing' | 'checksum';
/** The expected bytes and the input bytes read (through the adler32), or the problem and the bytes written by then. */
export type InflateExactResult = { ok: true; bytes: Uint8Array; consumed: number } | { ok: false; problem: InflateProblem; produced: number };
/** A PNG's IDAT chunks read in place: the whole file and the offset of its first IDAT chunk (-1: none). */
export interface IdatChunks {
  png: Uint8Array;
  first: number;
}

/** RFC 1950 adler32 of `bytes`, reduced every 4,096 bytes (the sums stay exact far below 2^53). */
function adler32(bytes: Uint8Array): number {
  let a = 1;
  let b = 0;
  for (let i = 0; i < bytes.length; ) {
    for (const end = Math.min(bytes.length, i + 4096); i < end; i++) {
      a += bytes[i] ?? 0;
      b += a;
    }
    a %= 65521;
    b %= 65521;
  }
  return ((b << 16) | a) >>> 0;
}

/**
 * Inflates a zlib stream into exactly `size` bytes with an own RFC 1951 decoder. The stream is a list of byte arrays
 * (never joined) or a PNG's IDAT chunks, walked lazily from the file with two integers of state, so the input side
 * allocates nothing per chunk. The result buffer is also the window, stored / fixed / dynamic blocks write into it
 * directly, and the first byte past `size` stops the decode ('overflow'). Also refused: a missing or bad zlib header
 * ('header'); damaged deflate data, including invalid code sets and a distance before the first byte ('damaged'); input
 * that ends inside the stream or its trailer ('truncated'); a complete stream of fewer bytes ('short'); an adler32 that
 * does not match the bytes ('checksum', one pass over the result: ~9 ms for 2048² RGBA); for byte arrays (the TexPort's
 * own texture, PSD channels), any byte after the trailer ('trailing'). IDAT bytes after a verified adler32 are ignored
 * unread, as libpng, Chromium and Pillow do. Allocates the `size`-byte result and 8,640 bytes of tables; work is
 * proportional to the input read and at most `size` bytes written, never to what the stream would expand to.
 */
export function inflateExact(input: readonly Uint8Array[] | IdatChunks, size: number): InflateExactResult {
  try {
    const end = inflateRaw(inputSegments(input), size, !('png' in input));
    if (end.written < size) return { ok: false, problem: 'short', produced: end.written };
    if (end.trailing > 0) return { ok: false, problem: 'trailing', produced: size };
    return adler32(end.out) === end.adler ? { ok: true, bytes: end.out, consumed: end.consumed } : { ok: false, problem: 'checksum', produced: size };
  } catch (e) {
    const { problem, written } = e as { problem?: RawProblem; written?: number };
    if (problem === undefined) throw e;
    return { ok: false, problem, produced: written ?? 0 };
  }
}
